import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDate } from '../../../utils/Utils';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { CANDIDATE_SOURCES } from '../../ats-candidates/config';
import { multiSelectFilter, tagFilterColumn } from '../../ats-candidates/config/tagColumns';
import { POOLS_ROOT } from './poolApi';

const writeOk = (res) => res?.data?.status !== false;

// Havuz listesi (CRUD). Sıralama sunucuda `name` ve varsayılan CreateDate.
export const useCandidatePoolsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`${POOLS_ROOT}?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: p } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(p);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const buildPayload = (values) => ({
    name: (values.name || '').trim(),
    description: values.description ? values.description.trim() : null,
  });

  const create = useCallback(async (values) => {
    try {
      const res = await api.post(POOLS_ROOT, buildPayload(values));
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const update = useCallback(async (id, values) => {
    try {
      const res = await api.put(`${POOLS_ROOT}/${id}`, buildPayload(values));
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      const res = await api.delete(`${POOLS_ROOT}/${id}`);
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('ats.candidatePool.name'),
      options: {
        filter: true, sort: true, sortKey: 'name',
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'description',
      label: t('ats.candidatePool.description'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'memberCount',
      label: t('ats.candidatePool.memberCount'),
      options: { filter: false, sort: false, customBodyRender: (v) => v ?? 0 },
    },
    {
      name: 'ownerName',
      label: t('ats.candidatePool.owner'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    dateFilterColumn('createDate', t('ats.candidatePool.createDate'), lang, { sortKey: 'createdate' }),
  ], [t, lang]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('ats.candidatePool.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('ats.candidatePool.description'), type: 'textarea', grid: 12, rows: 3 },
  ], [t]);

  return {
    fetching, loadFailed, data, pagination, columns, objectSchema, refresh, create, update, remove,
  };
};

// Havuz üyeleri — süzgeçler SPEC F2: fullName/location/currentTitle [like], tagId[in],
// source[in], experienceYears aralık, consentValid[eq].
export const usePoolMembersApi = (poolId, queryParams, tagCatalog) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!poolId) return;
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`${POOLS_ROOT}/${poolId}/members?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: p } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(p);
      })
      .finally(() => setFetching(false));
  }, [poolId, queryParams, refreshTrigger]);

  const columns = useMemo(() => {
    const sourceLabel = (s) => t(`ats.candidate.source.${s}`, { defaultValue: s });
    const likeCol = (name, label) => ({
      name,
      label,
      options: {
        filter: true, sort: false,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    });
    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      likeCol('fullName', t('ats.candidate.fullName')),
      likeCol('currentTitle', t('ats.candidate.currentTitle')),
      likeCol('location', t('ats.candidate.location')),
      numericFilterColumn('experienceYears', t('ats.candidate.experienceYears'), { decimals: 0, sort: false }),
      {
        name: 'source',
        label: t('ats.candidate.sourceLabel'),
        options: {
          ...multiSelectFilter(t('ats.candidate.sourceLabel'), CANDIDATE_SOURCES, sourceLabel),
          sort: false,
          customBodyRender: (v) => (v ? sourceLabel(v) : '-'),
        },
      },
      tagFilterColumn(t('ats.candidateTag.tags'), tagCatalog),
      {
        name: 'consentValid',
        label: t('ats.candidatePool.consentValid'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          filterOptions: {
            defaultOp: 'eq',
            display: (filterList, onChange, index, column) => {
              const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
              return (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>{t('ats.candidatePool.consentValid')}</InputLabel>
                  <Select
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value;
                      filterList[index] = v === '' ? [] : [v];
                      onChange(filterList[index], index, column);
                    }}
                  >
                    <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                    <MenuItem value="true">{t('common.yes')}</MenuItem>
                    <MenuItem value="false">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
          customBodyRender: (v) => (v === false
            ? t('ats.candidatePool.consentExpired')
            : t('ats.candidatePool.consentOk')),
        },
      },
      {
        name: 'consentExpiresAt',
        label: t('ats.candidate.consentExpiresAt'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
      },
      {
        name: 'addedByName',
        label: t('ats.candidatePool.addedBy'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
    ];
  }, [t, lang, tagCatalog]);

  return { fetching, loadFailed, data, pagination, columns, refresh };
};
